import { AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges,
    ChangeDetectionStrategy
} from "@angular/core";
import { PdfSrcType, ZoomType } from "ngx-extended-pdf-viewer";
import { MainAppService } from "src/modules/app-template/services/main-app.service";
import { PdfViewerBoundingBox } from "../../models/pdf-viewer-bounding-box.model";
import { PdfViewerPreset } from "../../models/pdf-viewer-preset.model";
import { Utils } from "src/modules/utils/shared/utils";

@Component({
  selector: 'app-pdf-viewer',
  standalone: false,
  templateUrl: './pdf-viewer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './pdf-viewer.component.scss',
})
export class PdfViewerComponent implements AfterViewInit, DoCheck, OnChanges, OnDestroy {

    app = inject(MainAppService);
    elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    @Input()
    src: PdfSrcType;
    @Input()
    allowPrint = true;
    @Input()
    zoom: ZoomType;
    @Input()
    page: number;
    @Input()
    boundingBoxes: PdfViewerBoundingBox[] = [];
    @Input()
    preset: PdfViewerPreset = PdfViewerPreset.developer;
    @Output()
    documentLoaded = new EventEmitter<void>();
    @Output()
    documentLoadFailed = new EventEmitter<any>();

    hasError = false;
    pdfLanguage = this.getPdfLanguage();
    pdfViewerVisible = true;

    private languageReloadTimer: ReturnType<typeof setTimeout>;

    get developerPreset(): boolean {
        return this.preset == PdfViewerPreset.developer;
    }

    private getPdfLanguage(): string {
        switch (this.app.language) {
            case "en":
                return "en-US";
            case "es":
                return "es-ES";
            case "fr":
                return "fr";
            default:
                return "de";
        }
    }

    ngDoCheck(): void {
        let language = this.getPdfLanguage();
        if (language == this.pdfLanguage) {
            return;
        }

        this.pdfLanguage = language;
        this.pdfViewerVisible = false;
        clearTimeout(this.languageReloadTimer);
        this.languageReloadTimer = Utils.setTimerOnce(100, () => this.pdfViewerVisible = true);
    }

    ngAfterViewInit(): void {
        this.scheduleRenderBoundingBoxes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.boundingBoxes || changes.src || changes.page || changes.zoom) {
            this.scheduleRenderBoundingBoxes();
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.languageReloadTimer);
    }

    onLoaded(): void {
        this.hasError = false;
        this.documentLoaded.emit();
        this.scheduleRenderBoundingBoxes();
    }

    onRendered(): void {
        this.scheduleRenderBoundingBoxes();
    }

    onError(error: any): void {
        this.hasError = true;
        this.documentLoadFailed.emit(error);
        this.clearBoundingBoxes();
    }

    @HostListener("window:keydown", ["$event"])
    onWindowKeyDown(event: KeyboardEvent): void {
        if (!this.isVisibleWithDocument()) {
            return;
        }

        if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key?.toLowerCase() == "s") {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }

    private isVisibleWithDocument(): boolean {
        if (this.src == null || this.src == "") {
            return false;
        }

        let rect = this.elementRef.nativeElement.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }

    private scheduleRenderBoundingBoxes(): void {
        Utils.setTimerOnce(1, () => this.renderBoundingBoxes());
    }

    private renderBoundingBoxes(): void {
        this.clearBoundingBoxes();

        for (let box of this.boundingBoxes ?? []) {
            let pageElement = this.elementRef.nativeElement.querySelector<HTMLElement>(`.page[data-page-number="${box.page}"]`);
            if (pageElement == null) {
                continue;
            }

            pageElement.style.position = 'relative';
            let color = box.color ?? '#ef4444';
            let element = document.createElement('div');
            element.className = 'app-pdf-bounding-box';
            element.style.left = box.rect.left + '%';
            element.style.top = box.rect.top + '%';
            element.style.width = box.rect.width + '%';
            element.style.height = box.rect.height + '%';
            element.style.borderColor = color;
            element.style.backgroundColor = this.withAlpha(color, 0.12);
            element.title = box.description ?? '';
            pageElement.appendChild(element);
        }
    }

    private clearBoundingBoxes(): void {
        for (let element of Array.from(this.elementRef.nativeElement.querySelectorAll('.app-pdf-bounding-box'))) {
            element.remove();
        }
    }

    private withAlpha(color: string, alpha: number): string {
        if (/^#[0-9a-f]{6}$/i.test(color)) {
            let r = parseInt(color.substring(1, 3), 16);
            let g = parseInt(color.substring(3, 5), 16);
            let b = parseInt(color.substring(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        return color;
    }
}
